import { useEffect, useState, useRef } from 'react';
import { Icon } from '@/components/Icon'
import { Textbox, Button, Spacing, HalfSpacing, ThreeQuarterSpacing } from '@/form/FormComponents';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useImmer } from 'use-immer';
import { useFormField } from '@/form/FormProvider';
import { randomId } from '@/shared/utils';

export function TextboxList({title, add_button_label, reorderable, start_with_one = true, key_value, multiline, height, autofocus, handleAddItem, ...args}) {

  const { name, value, setValue, error, setError, required, disabled, readOnly, obviated } = useFormField({
    validateValue: ({value, required}) => {
      if (key_value) {
        if ((value || []).some(a => (a[key_value[0].name].trim().length == 0 || a[key_value[1].name].trim().length == 0) && !(a[key_value[0].name].trim().length == 0 && a[key_value[1].name].trim().length == 0))) {
          return "Both fields are required for every item";
        }
      }
      if (required && (value || []).filter(a => key_value ? (a[key_value[0].name].trim().length > 0 && a[key_value[1].name].trim().length > 0) : a.trim().length > 0).length == 0) {
        return required;
      }
    },
    formatValue: (value, pretty) => {
      return (value || [])
        .filter(a => {
          if (!pretty) {
            return true;
          }
          if (key_value) {
            return a[key_value[0].name].trim().length > 0 || a[key_value[1].name].trim().length > 0;
          } else {
            return a.trim().length > 0;
          }
        })
        .map(a => {
          if (key_value) {
            return {
              [key_value[0].name]: pretty ? a[key_value[0].name].trim() : a[key_value[0].name],
              [key_value[1].name]: pretty ? a[key_value[1].name].trim() : a[key_value[1].name]
            };
          } else {
            return pretty ? a.trim() : a;
          }
        });
    },
    parseValue: (value) => value || [],
    // getFocusElement: ({name, parent}) => parent.querySelector(`[name="${name}"] .textbox`) || parent.querySelector(`[name="${name}"]`),
    getFocusElement: ({name, parent}) => parent.querySelector(`[name="${name}"] ~ .textbox_error_element`),
    default_value: start_with_one ? [(key_value ? key_value.reduce((acc, val) => { acc[val.name] = ''; return acc; }, {}) : "")] : [],
    ...args
  });

  const input_ref = useRef();

  useEffect(() => {
    if (autofocus) {
      if (input_ref.current && input_ref.current.value == "") {
        input_ref.current.focus();
      }
    }
  }, [autofocus])

  let [local_ids, updateLocalIds] = useImmer(value.map((_, index) => randomId()));

  if (value.length != local_ids.length) {
    updateLocalIds(draft => {
      while (draft.length < value.length) {
        draft.push(randomId())
      }
      while (draft.length > value.length) {
        draft.pop()
      }
    })
  }

  let [justAddedItem, setJustAddedItem] = useState(false);

  useEffect(function() {
    if (justAddedItem !== false) {
      requestAnimationFrame(() => {
        let addedItem = document.querySelector("div[name=\""+name+"\"] .textbox_list_item:nth-child("+(justAddedItem + 1)+")");
        let addedItemInput = addedItem.querySelector(".textbox");
        addedItemInput.focus();
        // if (justAddedItem == value.length - 1) {
        //   addedItem.closest(".nearest_scrollable_container").scrollTo(0, addedItem.offsetTop);
        // } else {
        let before_scrolltop = addedItem.closest(".nearest_scrollable_container")?.scrollTop;
        addedItem.scrollIntoViewIfNeeded();
        let after_scrolltop = addedItem.closest(".nearest_scrollable_container")?.scrollTop;
        if (addedItem.closest(".nearest_scrollable_container") && before_scrolltop != after_scrolltop) {
          addedItem.closest(".nearest_scrollable_container").scrollTop += 49 + 16;
        }
        // }
      });
      setJustAddedItem(false);
    }
  }, [justAddedItem])

  function handleAdd(index) {
    if (handleAddItem) {
      handleAddItem(doAddItems);
    } else {
      doAddItems();
    }

    function doAddItems(items) {
      let new_items = items ? items : [(key_value ? { [key_value[0].name]: "", [key_value[1].name]: "" } : "")];

      let draft = JSON.parse(JSON.stringify(value));
      new_items.forEach(new_item => {
        if (index) {
          draft.splice(index, 0, new_item);
          updateLocalIds(draft => {draft.splice(index, 0, randomId())})
          setJustAddedItem(index);
        } else {
          draft.push(new_item);
          updateLocalIds(draft => {draft.push(randomId())})
          setJustAddedItem(value.length);
        }
      })
      setValue(draft);
      setError(null);
    }
  }

  function onDragEnd(result) {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    setValue(reorder(
      value,
      result.source.index,
      result.destination.index
    ))
    updateLocalIds(draft => {
      draft = reorder(
        draft,
        result.source.index,
        result.destination.index
      );
      return draft;
    })
  }

  function handleTextChange(index, subkey) {
    return function(new_value) {
      let draft = JSON.parse(JSON.stringify(value));
      if (subkey) {
        draft[index][subkey] = new_value
      } else {
        draft[index] = new_value
      }
      setValue(draft);
      setError(null);
    }
  }

  function handleRemove(index, focus_previous_item) {
    let draft = JSON.parse(JSON.stringify(value));
    draft.splice(index, 1);
    setValue(draft);
    updateLocalIds(draft => {
      draft.splice(index, 1)
    })
    if (focus_previous_item && index > 0) {
      setJustAddedItem(index - 1);
    }
  }

  return (
    <>
      <label htmlFor={name}>{title}</label>
      <div name={name}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="questions">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
              >
                {value.map((item, index) => (
                  <Draggable key={local_ids[index]} draggableId={local_ids[index]} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={getStyleVerticalLock(provided.draggableProps.style)}
                        className={`textbox_list_item ${key_value ? "textbox_list_item_key_value" : ""} ${(key_value && !key_value[0].label) ? "no_labels" : ""} ${snapshot.isDragging ? "dragging" : ""}`}
                      >
                        <div {...provided.dragHandleProps} style={{display: (reorderable ? "" : "none")}}><Icon name="drag_indicator"></Icon></div>
                        <div>
                          {key_value ? 
                            <>
                              <Textbox title={key_value[0].label} value={value[index][key_value[0].name]} ref={index == 0 ? input_ref : null} placeholder={key_value[0].placeholder} onChange={handleTextChange(index, key_value[0].name)} onEnter={() => {
                                
                              }}/>
                              <ThreeQuarterSpacing/>
                              <Textbox title={key_value[1].label} value={value[index][key_value[1].name]} placeholder={key_value[1].placeholder} multiline={true} onChange={handleTextChange(index, key_value[1].name)}/>
                              {/* {index == value.length - 1 ? <HalfSpacing/> : <Spacing/>} */}
                            </>
                          : 
                            <>
                              <Textbox value={value[index]} ref={index == 0 ? input_ref : null} multiline={multiline} height={height} onChange={handleTextChange(index)} onKeyDown={(event) => {
                                if (event.key === 'Enter' && !event.shiftKey) {
                                  if (value[index].trim().length > 0 || true) {
                                    event.preventDefault();
                                    handleAdd(index + 1)
                                  }
                                }
                                if (event.key === 'Backspace' && value[index].trim().length == 0/* && value.length > 1*/) {
                                  event.preventDefault();
                                  handleRemove(index, true)
                                }
                              }}/>
                            </>
                          }
                        </div>
                        {!readOnly ? <div className="textbox_remove_item" onClick={() => {handleRemove(index)}} tabIndex={0} inert={(obviated || disabled) ? "true" : null}><Icon name="close"></Icon></div> : null}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
      {(readOnly && value.length == 0) ? <div className="textboxlist_placeholder">None</div> : null}
      {!readOnly ? (
        <div className="buttons" style={{paddingTop: (key_value || value.length > 0) ? "5px" : "1px"}}>
          <Button text={add_button_label} icon="plus" className={key_value ? "" : "small"} disabled={disabled} obviated={obviated} onClick={() => {handleAdd()}}/>
        </div>
      ) : null}
      <div className="textbox_error_element">
        {error ? <div className="input_error">{error}</div> : null}
      </div>
    </>
  )
}

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const getStyleVerticalLock = (style) => {
  return style?.transform
    ? ({ ...style, transform: `translate(0px, ${style.transform.split(',').pop()}` })
    : style
}